import type { RouteObject } from "react-router-dom";
import RouteGate from "@/components/feature/RouteGate";
import NotFound from "@/pages/NotFound";
import AumHome from "@/pages/aum-home/page";
import RevolvingGate from "@/pages/revolving-gate/page";
import About from "@/pages/about/page";
import FreeAngel from "@/pages/free-angel/page";
import LivingProfile from "@/pages/living-profile/page";
import Qualification from "@/pages/qualification/page";
import QualificationSlides from "@/pages/qualification-slides/page";
import Membership from "@/pages/membership/page";
import MemberOnboarding from "@/pages/member-onboarding/page";
import Journey from "@/pages/journey/page";
import WeeklyPractice from "@/pages/weekly-practice/page";
import Evidence from "@/pages/evidence/page";
import AIMentor from "@/pages/ai-mentor/page";
import Library from "@/pages/library/page";
import Dashboard from "@/pages/dashboard/page";
import Contact from "@/pages/contact/page";
import LivingNode from "@/pages/living-node/page";
import PortalPractice from "@/pages/portal-practice/page";
import OgQiHealingEntry from "@/pages/og-qi-healing/page";
import OgPortalPage from "@/pages/og-qi-healing/portal/page";
import NewCasePage from "@/pages/og-qi-healing/new-case/page";
import QiIntakePage from "@/pages/og-qi-healing/intake/page";
import CaseDetailPage from "@/pages/og-qi-healing/case-detail/page";
import FollowUpPage from "@/pages/og-qi-healing/follow-up/page";
import ReviewPage from "@/pages/og-qi-healing/review/page";
import WitnessesPage from "@/pages/og-qi-healing/witnesses/page";
import AdminRolesPage from "@/pages/og-qi-healing/roles/page";
import AdmRootPage from "@/pages/adm/page";
import AdmPeoplePage from "@/pages/adm/people/page";
import AdmRgPage from "@/pages/adm/rg/page";
import AdmOgPage from "@/pages/adm/og/page";
import AccountPage from "@/pages/account/page";
import EmailTestPage from "@/pages/email-test/page";
import AbaaWitnessPage from "@/pages/abaa-witness/page";
import { HumanParadisePage, LightTowerPage } from "@/pages/missions/page";

/**
 * 權限模型（V55 起）：
 *  - PUBLIC：明確批准公開。
 *  - COMING_SOON：未批准公開，以 Coming Soon 閘門攔截匿名訪客。
 *  - LOGIN_REQUIRED / ROLE_REQUIRED：由頁面自身 AuthGuard / AuthGate 攔截，保留原機制。
 *
 * 新增頁面時，預設一律 COMING_SOON（或 LOGIN_REQUIRED），不得預設 PUBLIC，
 * 直到 ABAA 明確批准 PUBLIC。
 */
const routes: RouteObject[] = [
  // ── PUBLIC ── 已明確批准公開
  { path: "/", element: <AumHome /> },
  { path: "/revolving-gate", element: <RevolvingGate /> },
  { path: "/og/portal", element: <OgPortalPage /> },
  { path: "/account", element: <AccountPage /> },
  { path: "/member/onboarding", element: <MemberOnboarding /> },
  { path: "/abaa-witness", element: <AbaaWitnessPage /> },
  { path: "/mission/human-paradise", element: <HumanParadisePage /> },
  { path: "/mission/light-towers", element: <LightTowerPage /> },

  // ── COMING_SOON ── 尚未批准公開，匿名訪客僅能看到 Coming Soon 遮罩
  { path: "/about", element: <RouteGate access="COMING_SOON"><About /></RouteGate> },
  { path: "/free-angel", element: <RouteGate access="COMING_SOON"><FreeAngel /></RouteGate> },
  { path: "/living-profile", element: <RouteGate access="COMING_SOON"><LivingProfile /></RouteGate> },
  { path: "/qualification", element: <RouteGate access="COMING_SOON"><Qualification /></RouteGate> },
  { path: "/qualification-slides", element: <RouteGate access="COMING_SOON"><QualificationSlides /></RouteGate> },
  { path: "/membership", element: <RouteGate access="COMING_SOON"><Membership /></RouteGate> },
  { path: "/journey", element: <RouteGate access="COMING_SOON"><Journey /></RouteGate> },
  { path: "/weekly-practice", element: <RouteGate access="COMING_SOON"><WeeklyPractice /></RouteGate> },
 { path: "/evidence", element: <Evidence /> },
  { path: "/living-node", element: <RouteGate access="COMING_SOON"><LivingNode /></RouteGate> },
  { path: "/ai-mentor", element: <RouteGate access="COMING_SOON"><AIMentor /></RouteGate> },
  { path: "/library", element: <RouteGate access="COMING_SOON"><Library /></RouteGate> },
  { path: "/dashboard", element: <RouteGate access="COMING_SOON"><Dashboard /></RouteGate> },
  { path: "/contact", element: <RouteGate access="COMING_SOON"><Contact /></RouteGate> },
  { path: "/og/qi-healing/witnesses", element: <WitnessesPage /> },

  // ── LOGIN_REQUIRED ── 既有登入保護（AuthGuard / 登入表單），保留原機制
  { path: "/portal-practice", element: <PortalPractice /> },
  { path: "/og/qi-healing", element: <OgQiHealingEntry /> },
  { path: "/og/qi-healing/new", element: <NewCasePage /> },
  { path: "/og/qi-healing/intake", element: <QiIntakePage /> },
  { path: "/og/qi-healing/case/:id", element: <CaseDetailPage /> },
  { path: "/og/qi-healing/follow-up/:token", element: <FollowUpPage /> },

  // ── ROLE_REQUIRED ── 既有 AuthGate（requireStaff / admin），保留原機制
  { path: "/og/qi-healing/review", element: <ReviewPage /> },
  { path: "/og/qi-healing/roles", element: <AdminRolesPage /> },

  // ── ADM Center ── 全站授權治理中心（頁面自身 AdmGate 依 scope 攔截）
  { path: "/adm", element: <AdmRootPage /> },
  { path: "/adm/people", element: <AdmPeoplePage /> },
  { path: "/adm/rg", element: <AdmRgPage /> },
  { path: "/adm/og", element: <AdmOgPage /> },

  // ── 測試工具 ── Email 寄送測試（臨時，測試完可移除）
  { path: "/email-test", element: <EmailTestPage /> },

  // ── RG AI Angel Alpha／Beta 測試入口 ── ABAA 已批准在 main 開放測試
  // 正式服務入口 /ai-mentor 仍保留 Coming Soon，待營運驗收後另行開放。
  { path: "/rg-ai-test", element: <AIMentor /> },

  { path: "*", element: <NotFound /> },
];

export default routes;

